import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

export type TFilterPreviewState = 'idle' | 'loading' | 'error';
interface IProps {
  onSubmit?: () => void;
  state: TFilterPreviewState;
  total: number | null;
  isDisabled?: boolean;
}

const FilterPreviewResults: React.FC<IProps> = ({ onSubmit, state, total, isDisabled }) => {
  const intl = useIntl();
  const resultsMessage = intl.formatMessage(
    {
      defaultMessage: 'Show {total, plural, one {# RV} other {# RVs}}',
      id: 'JZeWmw',
      description: 'Search > Filters: label with preview results',
    },
    { total },
  );
  const defaultMessage = intl.formatMessage({
    defaultMessage: 'Show RVs',
    id: 'iYo5qy',
    description: 'Search > Filters: default label',
  });
  const noResultsMessage = intl.formatMessage({
    defaultMessage: 'No results',
    id: 'Ukeqf0',
    description: 'Search > Filters: label with no results',
  });

  const handleSubmit = useCallback(() => {
    if (state === 'loading') return;
    onSubmit?.();
  }, [state, onSubmit]);

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    // Prevent click event from bubbling up to elements underneath the modal
    event.stopPropagation();
  }, []);

  // Determine button label based on state and total
  const getButtonLabel = () => {
    if (state === 'error') {
      return noResultsMessage; // Show "No results" when there's an error
    }
    if (isDisabled) {
      return defaultMessage;
    }
    if (total !== null) {
      return total > 0 ? resultsMessage : noResultsMessage;
    }
    return defaultMessage;
  };

  // Determine if button should be disabled
  const isButtonDisabled =
    (state === 'idle' && total === 0) || // No results in idle state
    state === 'error' || // Disable when there's an error
    isDisabled; // External disabled prop

  return (
    <Button
      className="md:w-auto"
      onClick={handleSubmit}
      onClickCapture={handleClickCapture}
      label={getButtonLabel()}
      variant={EButtonColorVariant.Primary}
      loading={state === 'loading'}
      disabled={isButtonDisabled}
    />
  );
};

export default FilterPreviewResults;
